/**
 * The parental-controls CTA (fix Z2.4) — "when parental controls are
 * enabled let's show the CTA with tooltip near the theme-changing button so
 * that clicking it the user can see what parental controls are enabled —
 * inside Info it's difficult to discover" (owner, 2026-10-06).
 *
 * The popover lives INSIDE the rail: `rail/Rail/View.tsx`'s own card is
 * `overflow: 'hidden'` and this bar is its LAST child, so a box anchored off
 * the button's own bottom paints over the tab body and never spills past the
 * card's edge — the OLD `GameRulesComponent`'s own popover (`top: -100%..`,
 * `left: -50%`) could not make that promise, which is the bug Z2 replaces.
 *
 * DESKTOP RAIL ONLY, like the sound/theme row it joins — the PHONE's
 * `Toolbar` is a separate face (M1b) and is out of scope here.
 *
 * Model + view in one file: the unit convention's own exception for a leaf
 * this small (same shape as `arenaPause.ts`'s button).
 */
import React, {useCallback, useEffect, useState} from 'react';
import {Platform, StyleSheet, View} from 'react-native';

import {useLiveGame} from '@/Store/liveGameStore';

import Icon from '../../../components/Icon';
import {RADIUS, SPACE} from '../../../theme/tokens';
import {useV2Theme} from '../../../theme/useV2Theme';
import {Txt} from '../../../ui';
import ParentalRulesList from '../../info/ParentalRules/View';
import {parentalRulesFor} from '../../info/ParentalRules/rules';
import IconButtonView from './IconButton.View';

/** mirrors `IconButton.View`'s own 36 px button — the popover anchors off it */
const BUTTON = 36;
const HEADER_ICON_SIZE = 17;

function ParentalControlsButton() {
  const rules = useLiveGame(s => s.session.customRules);
  const tokens = useV2Theme(s => s.tokens);
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen(o => !o), []);

  useEffect(() => {
    if (!open || Platform.OS !== 'web') {
      return undefined;
    }
    const onKeyDown = (e: any) => {
      if (e.key === 'Escape') {
        close();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  if (rules.length === 0) {
    return null;
  }

  return (
    <View style={styles.anchor}>
      <IconButtonView
        icon="shield"
        label="Parental controls"
        tooltip={open ? undefined : 'Parental controls on'}
        active={open}
        onPress={toggle}
        testID="v2-action-parental"
      />
      {open && (
        <View
          testID="v2-parental-popover"
          style={
            [
              styles.popover,
              {
                borderColor: tokens.cardline,
                backgroundColor: tokens.surface,
                boxShadow: tokens.sh2,
              },
            ] as any
          }>
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <Icon name="shield" size={HEADER_ICON_SIZE} color={tokens.ink2} />
              <Txt variant="rowName">Parental controls</Txt>
            </View>
            <IconButtonView
              icon="close"
              label="Close"
              onPress={close}
              testID="v2-parental-close"
            />
          </View>
          <Txt variant="caption" tone="muted">
            Set for this account · active in your games
          </Txt>
          <ParentalRulesList rules={parentalRulesFor(rules)} compact />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  anchor: {
    position: 'relative',
  },
  popover: {
    position: 'absolute',
    bottom: BUTTON + SPACE.xs,
    right: 0,
    width: 300,
    borderRadius: RADIUS.event,
    borderWidth: 1,
    padding: SPACE.md,
    gap: SPACE.sm,
    zIndex: 20,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACE.xs,
  },
});

export default React.memo(ParentalControlsButton);
